import {
  useDesignLanguagePerClass,
  CARET_PLACEMENTS,
  Option,
  Override,
  Skeleton,
  Text,
  Tooltip,
  Icon,
  useIsDesignLanguage,
  DesignLanguage,
} from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode, useContext, useEffect, useRef, useState, MouseEvent } from 'react'
import { CardExpiryStatus, isCashAppPay as checkCashAppPay } from '@afterpay/types'
import { Cohort, Country } from '@afterpaytouch/portal-api/types'
import { Route } from '../../config/router'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { EnrichedCardDetails, isSavedCard, NewCard, PageLayout, PaymentMethodType, SavedCard } from '../../model'
import { PageNames, PaymentMethodTypes, TrackingEvent } from '../../model/monitoring'
import {
  setCashTooltipShown,
  useActiveCashAppPay,
  useAppDispatch,
  useAppRouter,
  useCohort,
  useExperiment,
  useHasSavedPaymentMethods,
  useIsDisabledUpdatingCheckout,
  useSavedBankAccounts,
  useSavedCards,
  useSelectedPaymentMethod,
  useShouldShowCashAppTooltip,
} from '../../state'
import { getCardBrandFromCardNumber } from '@afterpaytouch/form/lib/fields/Card/utils'
import { PaymentMethodApplePay } from '../PaymentMethodApplePay'
import { PaymentMethodBankAccount } from '../PaymentMethodBankAccount'
import { PaymentMethodCard } from '../PaymentMethodCard'

import styles from './style.module.css'
import clsx from 'clsx'
import { PaymentMethodCashAppPay } from '../PaymentMethodCashAppPay'
import { useCheckoutSizes } from '../../utils/responsive'
import { TransitionContext } from '../PageTransition'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { PaymentMethodNoCard } from '../PaymentMethodNoCard'
import { isCallable } from '@afterpay/utils'
import { useTranslation } from '../../utils/convergence'

interface Props {
  layout?: PageLayout
  optionRadiusOverride?: Override
  negativeMargins?: boolean
  isLoading?: boolean
  showFloatingHeader?: boolean
  changeLabel?: string | ReactNode | (() => ReactNode)
  warningBorder?: boolean
  showErrorMessage?: boolean
  labelHeader?: ReactNode | (() => ReactNode)
  isGALayout?: boolean
}

const toCardDetails = (card: NewCard): Omit<EnrichedCardDetails, 'cardBrand'> & { cardBrand?: EnrichedCardDetails['cardBrand'] } => {
  const expiryYear = parseFloat(card.cardExpiryYear)
  const expiryMonth = parseFloat(card.cardExpiryMonth)
  const expiryDate = `${card.cardExpiryMonth}/${card.cardExpiryYear}`
  return {
    cardholderName: card.cardHolderName,
    maskedPan: card.cardNumber,
    expiryYear,
    expiryMonth,
    expiryDate,
    token: '',
    cvvRequired: false,
    preferred: false,
    issuingBank: '',
    cardBrand: getCardBrandFromCardNumber(card.cardNumber),
    creditCardType: card.creditCardType ?? '',
    countryCode: card.address?.country as Country,
    cardExpiryStatus: CardExpiryStatus.OK,
    error: null,
    paymentMethodType: PaymentMethodType.CREDIT_CARD,
  }
}

export const PaymentMethod: FunctionComponent<Props> = ({
  layout,
  optionRadiusOverride,
  negativeMargins = true,
  isLoading = false,
  showFloatingHeader = true,
  changeLabel,
  warningBorder,
  labelHeader,
  showErrorMessage = true,
  isGALayout = false,
}) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const savedCards = useSavedCards()
  const savedBankAccounts = useSavedBankAccounts()
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()
  const { shouldShowCashAppAccountLinkingTooltip, saveToTags } = useShouldShowCashAppTooltip()
  const method = useSelectedPaymentMethod()
  const cashTooltipExperiment = useExperiment('CheckoutSummaryCashTooltip')
  const { instrument } = useMonitoringWithEnduringEventProperties({
    pageName: PageNames.SUMMARY,
    paymentMethodType: PaymentMethodTypes[method?.type as PaymentMethodType],
  })
  const dispatch = useAppDispatch()
  const cohort = useCohort()
  const isBronteDesignLangauge = useIsDesignLanguage(DesignLanguage.Bronte)
  const isCashOnlyCohort = cohort === Cohort.CashOnly
  const activeCashAppPay = useActiveCashAppPay()
  // TODO: map this eligibility checker in a bird eye view
  const isPaymentMethodOptionClickable = isBronteDesignLangauge || !isCashOnlyCohort || (isCashOnlyCohort && activeCashAppPay.length === 0)
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const id = 'cash-app-pay-tooltip'

  const tooltipRef = useRef(null)
  const { textSize } = useCheckoutSizes()

  const [showTooltip, setShowTooltip] = useState(false)

  const status = useContext(TransitionContext)

  const [showCashAppPaymentMethodTooltip, setShowCashAppPaymentMethodTooltip] = useState(false)
  const cashAppPaymentMethodToolTipContent = (): JSX.Element => <Text size={textSize}>{t('paymentMethod:cashAppPaymentMethodTooltip')}</Text>
  const cashAppPaymentMethodTooltipRef = useRef(null)
  const isCashAppPay = method?.type === PaymentMethodType.CASH_APP_PAY && 'cashAppPay' in method && checkCashAppPay(method.cashAppPay)

  const closeCashAppPaymentMethodTooltip = (): void => {
    setShowCashAppPaymentMethodTooltip(false)
  }

  const toggleCashAppPaymentMethodTooltip = (e: MouseEvent): void => {
    e.stopPropagation()
    setShowCashAppPaymentMethodTooltip(!showCashAppPaymentMethodTooltip)
  }

  useEffect(() => {
    if (status === 'entered' && shouldShowCashAppAccountLinkingTooltip) {
      setShowTooltip(true)
      saveToTags()
    }
  }, [status, shouldShowCashAppAccountLinkingTooltip, saveToTags])

  useEffect(() => {
    dispatch(setCashTooltipShown(showTooltip))
  }, [dispatch, showTooltip])

  const isDisabledUpdatingCheckout = useIsDisabledUpdatingCheckout()
  const isDisabled = isDisabledUpdatingCheckout || isLoading

  const paymentMethodButtonStyle = clsx(negativeMargins ? styles.paymentMethodButtonNegativeMargins : designLanguageStyles.paymentMethodButton)

  const handlePaymentMethodClick = instrument(TrackingEvent.PRESSED_SUMMARY_CHANGE_PAYMENT_METHOD, () => {
    const route = hasSavedPaymentMethods ? Route.PAYMENT_METHOD : Route.NEW_PAYMENT_METHOD
    router.push(route)
  })

  const getPaymentMethodItem = (): ReactNode => {
    if (method?.type === PaymentMethodType.APPLE_PAY) {
      return <PaymentMethodApplePay iconSize='Small' />
    }
    if (isCashAppPay) {
      return (
        <PaymentMethodCashAppPay
          status={method.cashAppPay.status}
          cashtag={method.cashAppPay.cashtag}
          cashAppPaymentMethodTooltipRef={cashAppPaymentMethodTooltipRef}
          showCashAppPaymentMethodTooltip={showCashAppPaymentMethodTooltip}
          setShowCashAppPaymentMethodTooltip={setShowCashAppPaymentMethodTooltip}
          isGALayout={isGALayout}
        />
      )
    }
    if (method?.type === PaymentMethodType.CREDIT_CARD && 'card' in method) {
      const card = isSavedCard(method.card) ? savedCards.find((saved) => saved.token === (method.card as SavedCard).token) : toCardDetails(method.card)
      const errorKey = card?.error === 'invalidBrand' ? null : card?.error

      return <PaymentMethodCard card={card} errorKey={errorKey} iconSize='Small' disabled={isDisabled} showErrorMessage={showErrorMessage} />
    }
    if (method?.type === PaymentMethodType.ACH_BANK_ACCOUNT && 'account' in method) {
      const account = savedBankAccounts?.find((account) => account.id === method.account.id)
      return <PaymentMethodBankAccount iconSize='Small' account={account} errorKey={account?.error} disabled={isDisabled} showErrorMessage={showErrorMessage} />
    }
    return <PaymentMethodNoCard />
  }

  const renderLabelHeader = (): ReactNode => {
    if (labelHeader !== undefined) {
      return isCallable(labelHeader) ? labelHeader() : labelHeader
    }
  }

  const renderChangeLabel = (): ReactNode | void => {
    if (typeof changeLabel === 'string' && changeLabel.length > 0) {
      return (
        <Text renderAs='span' bold size='XS' underline testNameSpace='change-label'>
          {changeLabel}
        </Text>
      )
    }

    if (changeLabel !== undefined) {
      return isCallable(changeLabel) ? changeLabel() : changeLabel
    }
  }
  const tooltipContent = (): JSX.Element => {
    return (
      <div
        className={styles.tooltip}
        onClick={() => {
          setShowTooltip(false)
        }}
      >
        <Text size={textSize}>{cashTooltipExperiment.control ? t('paymentMethod:cashPayTooltip') : t('paymentMethod:cashPayTooltipExperiment')}</Text>
      </div>
    )
  }

  const renderTooltip = (): JSX.Element | null =>
    showTooltip ? (
      <Tooltip
        id={id}
        content={tooltipContent()}
        caretPlacement={CARET_PLACEMENTS.BOTTOM_END}
        triggerRef={tooltipRef}
        usePortal={false}
        bubbleSize={{ width: '100%' }}
        skidding={-19}
        verticalOffset={16}
      />
    ) : null

  const paymentMethodItem = getPaymentMethodItem()

  const hasChangeLabel = changeLabel !== undefined

  const hasBothChangeAndHeaderLabels = hasChangeLabel && labelHeader !== undefined
  const optionProps = isPaymentMethodOptionClickable
    ? {
        onClick: handlePaymentMethodClick,
        ariaLabel: t('common:changePaymentMethod'),
        disabled: isDisabled,
        changeLabel: renderChangeLabel(),
        override: optionRadiusOverride,
        ...(hasBothChangeAndHeaderLabels && { fullWidthContent: paymentMethodItem }),
        tooltipRef,
        warningBorder,
      }
    : {}

  const maskElementProps = isPaymentMethodOptionClickable
    ? {
        actionName: 'Change Payment Method',
      }
    : {}

  const renderCashAppPaymentMethodTooltip = (): JSX.Element | null => {
    return showCashAppPaymentMethodTooltip ? (
      <div className={styles.tooltipWrapper}>
        <Tooltip
          id='cashapp-payment-method-tooltip'
          triggerRef={cashAppPaymentMethodTooltipRef}
          caretPlacement={CARET_PLACEMENTS.BOTTOM_START}
          testNameSpace='cashapp-payment-method-tooltip'
          usePortal={false}
          content={cashAppPaymentMethodToolTipContent()}
          skidding={90}
          hasCaret={true}
          useMobileView={false}
          bubbleSize={{ width: '100%' }}
          closeOnOutsideClick={true}
          onClose={closeCashAppPaymentMethodTooltip}
        />
      </div>
    ) : null
  }

  const renderText = (): JSX.Element => {
    const paymentMethodItem = getPaymentMethodItem()

    if (isGALayout) {
      return (
        <div className={styles.newLayoutContainer}>
          <div className={styles.newLayoutLeftPart}>
            <Text bold color='Black' testNameSpace='payment-method-pay-with'>
              {t('paymentMethod:payWith')}
            </Text>
            {isCashAppPay && (
              <button
                data-testid='cashapp-payment-method-tooltip-button'
                ref={cashAppPaymentMethodTooltipRef}
                className={styles.paymentMethodTooltipIcon}
                onClick={toggleCashAppPaymentMethodTooltip}
              >
                <Icon.Info size='Small' />
              </button>
            )}
          </div>
          <div className={styles.newLayoutRightPart}>{paymentMethodItem}</div>
        </div>
      )
    }

    return (
      <div className={designLanguageStyles.paymentItemWrapper}>
        {renderLabelHeader()}
        {!hasBothChangeAndHeaderLabels && paymentMethodItem}
        {isLoading && <Skeleton />}
      </div>
    )
  }

  return (
    <div className={paymentMethodButtonStyle} aria-describedby={id}>
      <MaskedElement {...maskElementProps}>
        <Option
          {...(showFloatingHeader && { floatingHeader: t('common:paymentMethod') })}
          text={renderText}
          testNameSpace='payment-method'
          stretch={isGALayout}
          {...optionProps}
        />
      </MaskedElement>
      {layout === PageLayout.Summary && method !== undefined && (
        <TrackOnMount eventName={TrackingEvent.VIEWED_DEFAULT_PAYMENT_METHOD} eventProperties={{ type: method.type }} />
      )}
      {renderTooltip()}
      {renderCashAppPaymentMethodTooltip()}
    </div>
  )
}
